import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getDesktopBridge } from "../../utils/desktopBridge";
import { initDesktopApiBase } from "../../service/CallCenterApi";
import { isDesktopApp } from "../../utils/isDesktop";
import { WASL_DEFAULT_BACKEND_URL } from "../../branding/waslBrand";

/**
 * Lets desktop users set the API backend before sign-in (fresh installs / shared installers).
 */
const DesktopBackendUrlField = ({ compact = false }) => {
  const [backendUrl, setBackendUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const bridge = getDesktopBridge();
    if (!bridge?.getBackendUrl) {
      setLoaded(true);
      return;
    }
    bridge
      .getBackendUrl()
      .then((url) => setBackendUrl(url || WASL_DEFAULT_BACKEND_URL))
      .catch(() => setBackendUrl(WASL_DEFAULT_BACKEND_URL))
      .finally(() => setLoaded(true));
  }, []);

  if (!isDesktopApp() || !loaded) {
    return null;
  }

  const handleSave = async (event) => {
    event.preventDefault();
    const bridge = getDesktopBridge();
    if (!bridge?.setBackendUrl) {
      return;
    }
    setSaving(true);
    try {
      const normalized = await bridge.setBackendUrl(backendUrl.trim());
      setBackendUrl(normalized);
      await initDesktopApiBase();
      toast.success("Server URL saved. Try signing in again.");
    } catch (error) {
      toast.error(error?.message || "Failed to save server URL");
    } finally {
      setSaving(false);
    }
  };

  if (compact) {
    return (
      <form onSubmit={handleSave} className="mb-6 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Desktop server</p>
        <p className="mb-3 text-xs leading-relaxed text-slate-600">
          Point WASL at the same backend you use in the browser before signing in.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="url"
            required
            value={backendUrl}
            onChange={(event) => setBackendUrl(event.target.value)}
            placeholder={WASL_DEFAULT_BACKEND_URL}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          />
          <button
            type="submit"
            disabled={saving}
            className="shrink-0 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save URL"}
          </button>
        </div>
      </form>
    );
  }

  return null;
};

export default DesktopBackendUrlField;
